import { DOC_TYPE_OPTIONS } from '@/constant/options';
import type { AssessmentClient } from '@/pages/application/credit-analyst/pages/calculations/types/applicant.type';
import type {
  ClassificationStatus,
  DocumentSources,
  ExtractOutcome,
  PortalDocument,
} from '../types/portal-documents.type';

export const UNCATEGORIZED = 'UNCATEGORIZED';
export const UNASSIGNED_LABEL = 'Unassigned';
/** The tracker polls while anything is received or classifying. */
export const PORTAL_POLL_MS = 15_000;

const CLOSED: ReadonlySet<ClassificationStatus> = new Set(['DISMISSED', 'SUPERSEDED']);
export const isClosed = (d: PortalDocument): boolean => CLOSED.has(d.status);

export const formatDocType = (docType: string): string =>
  docType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

/** Shared DOC_TYPE_OPTIONS label when there is one ("FTC Document"), else the title-cased enum. */
export const docTypeLabel = (docType: string): string =>
  DOC_TYPE_OPTIONS.find((o) => o.value === docType)?.label ?? formatDocType(docType);

/**
 * The only types the backend can file into a Supporting Docs bucket (DESTINATION_MAP in
 * brokercopilot-backend src/modules/documents/utils/document-copy.utils.ts). Any other type
 * is rejected with 400 "has no Supporting Docs bucket", so "Correct type" must not offer it.
 */
export const FILEABLE_DOC_TYPES: ReadonlySet<string> = new Set([
  'IDENTITY',
  'BANK_STATEMENT',
  'PAYSLIP',
  'EMPLOYMENT_DOCUMENT',
  'TAX_DOCUMENT',
  'CREDIT_CARD_STATEMENT',
  'TENANCY_AGREEMENT',
  'MORTGAGE_STATEMENT',
  'RENTAL_INCOME_STATEMENT',
  'PROPERTY_PURCHASE_DOCUMENT',
  'PROPERTY_OUTGOINGS',
  'PERSONAL_LOAN_STATEMENT',
  'FTC_DOCUMENT',
]);
export const correctTypeOptions = DOC_TYPE_OPTIONS.filter((o) => FILEABLE_DOC_TYPES.has(o.value));

export const portalDocumentName = (d: PortalDocument): string => d.proposedName ?? d.originalFilename;

const newestFirst = (a: PortalDocument, b: PortalDocument): number =>
  (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0);

export function matchesSearch(d: PortalDocument, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return [portalDocumentName(d), d.originalFilename, d.itemLabel].some((s) => (s ?? '').toLowerCase().includes(q));
}

export interface PortalDocumentGroup {
  docType: string;
  documents: PortalDocument[];
}

/** Open documents by effective type (A–Z by label, uncategorised last); closed ones apart. Newest first. */
export function groupPortalDocuments(docs: PortalDocument[]): { groups: PortalDocumentGroup[]; closed: PortalDocument[] } {
  const byType = new Map<string, PortalDocument[]>();
  const closed: PortalDocument[] = [];
  for (const d of docs) {
    if (isClosed(d)) {
      closed.push(d);
      continue;
    }
    const key = d.effectiveDocType ?? UNCATEGORIZED;
    byType.set(key, [...(byType.get(key) ?? []), d]);
  }
  const groups = [...byType.entries()]
    .map(([docType, documents]) => ({ docType, documents: [...documents].sort(newestFirst) }))
    .sort((a, b) =>
      a.docType === UNCATEGORIZED ? 1 : b.docType === UNCATEGORIZED ? -1 : docTypeLabel(a.docType).localeCompare(docTypeLabel(b.docType)),
    );
  return { groups, closed: closed.sort(newestFirst) };
}

/** applicantId → display name, in applicant order (PRIMARY first). */
export function applicantLabels(applicants: AssessmentClient[]): Map<string, string> {
  const sorted = [...applicants].sort((a, b) => (a.applicantType === 'PRIMARY' ? 0 : 1) - (b.applicantType === 'PRIMARY' ? 0 : 1));
  return new Map(
    sorted.map((a) => {
      const first = a.applicantDetail?.firstName || a.client?.firstName || '';
      const last = a.applicantDetail?.lastName || a.client?.lastName || '';
      const name = `${first} ${last}`.trim();
      return [a.id, name || (a.applicantType === 'PRIMARY' ? 'Primary applicant' : 'Co-applicant')];
    }),
  );
}

export const applicantLabel = (applicantId: string | null, labels: Map<string, string>): string =>
  (applicantId && labels.get(applicantId)) || UNASSIGNED_LABEL;

/** The backend refuses dismiss outside NEEDS_REVIEW/FAILED and while a copy is live in Supporting Docs. */
export const canDismiss = (d: PortalDocument): boolean =>
  (d.status === 'NEEDS_REVIEW' || d.status === 'FAILED') && !d.liveCopy;

export const STATUS_TAG: Record<ClassificationStatus, { label: string; color: string }> = {
  PENDING: { label: 'Processing', color: 'processing' },
  COMPLETED: { label: 'Completed', color: 'success' },
  NEEDS_REVIEW: { label: 'Needs review', color: 'warning' },
  FAILED: { label: 'Failed', color: 'error' },
  DISMISSED: { label: 'Dismissed', color: 'default' },
  SUPERSEDED: { label: 'Superseded', color: 'default' },
};

const OUTCOME: Record<ExtractOutcome, string> = {
  DONE: 'Extracted',
  SKIPPED_NO_SCHEMA: 'Not extracted (no schema)',
  SKIPPED_TOO_LONG: 'Not extracted (too long)',
  SKIPPED_NO_CONTENT: 'Not extracted (unreadable)',
  EMPTY: 'Nothing extracted',
  ERROR: 'Extraction error',
};
export const extractOutcomeLabel = (o: ExtractOutcome | null): string | null => (o ? OUTCOME[o] ?? o : null);

export function portalEmptyListCopy(portal: DocumentSources['portal']): string {
  const { received, classifying, failed } = portal.stages;
  if (portal.total === 0) return 'No portal documents yet';
  if (received + classifying > 0) return 'Uploads are being processed. They appear here once classified.';
  if (failed > 0) return 'Some uploads could not be filed. Check Document Requests for details.';
  return 'No portal documents yet';
}

export const isPortalProcessing = (p: DocumentSources['portal']): boolean => p.stages.received + p.stages.classifying > 0;

export interface ExplorerTreeNode {
  key: string;
  title: string;
  count?: number;
  isLeaf?: boolean;
  documentId?: string;
  children?: ExplorerTreeNode[];
}

/** Spec §4.5: applicant (applicant order, Unassigned last) → effective type → open documents, newest first. */
export function buildPortalExplorerTree(docs: PortalDocument[], labels: Map<string, string>, search: string): ExplorerTreeNode[] {
  // Grouped by id, not label: two unnamed co-applicants share a label but are different people.
  const UNASSIGNED_KEY = '__unassigned';
  const order = [...labels.keys(), UNASSIGNED_KEY];
  const byApplicant = new Map<string, Map<string, PortalDocument[]>>();
  for (const d of docs) {
    if (isClosed(d) || !matchesSearch(d, search)) continue;
    const id = d.applicantId && labels.has(d.applicantId) ? d.applicantId : UNASSIGNED_KEY;
    const type = d.effectiveDocType ?? UNCATEGORIZED;
    const types = byApplicant.get(id) ?? new Map<string, PortalDocument[]>();
    types.set(type, [...(types.get(type) ?? []), d]);
    byApplicant.set(id, types);
  }
  return [...byApplicant.entries()]
    .sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
    .map(([id, types]) => {
      const who = id === UNASSIGNED_KEY ? UNASSIGNED_LABEL : applicantLabel(id, labels);
      const children = [...types.entries()]
        .sort(([a], [b]) => (a === UNCATEGORIZED ? 1 : b === UNCATEGORIZED ? -1 : docTypeLabel(a).localeCompare(docTypeLabel(b))))
        .map(([type, list]) => ({
          key: `a:${id}|t:${type}`,
          title: docTypeLabel(type),
          count: list.length,
          children: [...list].sort(newestFirst).map((d) => ({
            key: `d:${d.documentId}`,
            title: portalDocumentName(d),
            isLeaf: true,
            documentId: d.documentId,
          })),
        }));
      return { key: `a:${id}`, title: who, count: children.reduce((n, c) => n + c.count, 0), children };
    });
}
